import { useCallback, useMemo, useState } from "react";
import { useSelectedWatchlist } from "./useSelectedWatchlist";
import { Watchlist } from "./useWatchlists";
import { useTouchLayout } from "../../../../hooks/useTouchLayout";

/**
 * How many watchlists get their own tab before the rest live in the dropdown.
 * A phone has room for one watchlist tab alongside Trending and Top Gainers —
 * three would push the market tabs off-screen — so touch layouts switch lists
 * through the dropdown instead.
 */
const MAX_WATCHLIST_TABS_POINTER = 3;
const MAX_WATCHLIST_TABS_TOUCH = 1;

const STORAGE_KEY = "insight:watchlist-tab-slots";

const readStoredSlots = (): string[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === "string") : [];
  } catch {
    return [];
  }
};

const writeStoredSlots = (slotIds: string[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(slotIds));
  } catch {
    // Slot assignment just won't survive to the next launch.
  }
};

interface WatchlistTabs {
  watchlists: Watchlist[];
  /** The watchlists currently pinned to a tab, in tab order. */
  slots: Watchlist[];
  assignSlot: (slotIndex: number, watchlistId: string) => void;
  /** Mark a watchlist as the app-wide default target (used by ViewTicker). */
  selectWatchlist: (watchlistId: string | null) => void;
  isLoading: boolean;
}

/**
 * Pins up to three watchlists to their own dashboard tabs so they're one click
 * away rather than two. Each tab can be reassigned to any watchlist via its
 * dropdown; duplicates across slots are allowed, since which list a user wants
 * in front of them at a given moment is their call.
 */
export function useWatchlistTabs(): WatchlistTabs {
  const { watchlists, selectWatchlist, isLoading } = useSelectedWatchlist();
  const [storedSlotIds, setStoredSlotIds] = useState<string[]>(readStoredSlots);
  const isTouch = useTouchLayout();
  const maxTabs = isTouch ? MAX_WATCHLIST_TABS_TOUCH : MAX_WATCHLIST_TABS_POINTER;

  // Derived, never trusted from storage: ids for deleted watchlists drop out,
  // and any remaining slots fill with lists not already on screen so the
  // default arrangement shows different watchlists.
  const slots = useMemo(() => {
    const slotCount = Math.min(watchlists.length, maxTabs);
    if (slotCount === 0) return [];

    const byId = new Map(watchlists.map((watchlist) => [watchlist.id, watchlist]));
    const resolved = storedSlotIds
      .filter((id) => byId.has(id))
      .slice(0, slotCount)
      .map((id) => byId.get(id) as Watchlist);

    for (const watchlist of watchlists) {
      if (resolved.length >= slotCount) break;
      if (!resolved.some((slot) => slot.id === watchlist.id)) {
        resolved.push(watchlist);
      }
    }

    return resolved;
  }, [watchlists, storedSlotIds, maxTabs]);

  const assignSlot = useCallback(
    (slotIndex: number, watchlistId: string) => {
      const nextSlotIds = slots.map((slot) => slot.id);
      nextSlotIds[slotIndex] = watchlistId;
      setStoredSlotIds(nextSlotIds);
      writeStoredSlots(nextSlotIds);
    },
    [slots],
  );

  return { watchlists, slots, assignSlot, selectWatchlist, isLoading };
}
